/**
 * The Order Queue's manual-order entry points (Phase 104, D-03 / D-22 / D-26),
 * after "Create Batch" in the queue's action row:
 *
 *   + Manual order    Drafts (N)    Awaiting artwork (N) -> On hold
 *
 * "Drafts (N)" opens the drafts list; "Awaiting artwork (N)" goes to
 * Production Issues, On Hold, filtered to art holds (held manual orders live
 * there, the queue does not list them). The destination is written on the
 * button (the On hold tab's own name), not hidden in a tooltip. Each count
 * button hides at 0.
 *
 * OrdersPage mounts this only for orders.create holders, so the badges
 * request is never sent for a role the procedure would refuse. The route
 * guard and every manualOrders procedure re-check.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { QUEUE_ATTENTION_BUTTON_CLASS } from '@/features/production-orders/components/orders/queue-attention-button';
import { useManualOrderBadges } from '../hooks/useManualOrderBadges';
import { DraftsDialog } from './DraftsDialog';

/** Production Issues, On Hold tab, art reasons preselected (OnHoldList reads `reason`). */
const WAITING_ON_ART_HREF = '/production/issues?tab=on-hold&reason=art';

interface ManualOrderQueueActionsProps {
  locationId: string | undefined;
}

export function ManualOrderQueueActions({ locationId }: ManualOrderQueueActionsProps) {
  const { t } = useTranslation('production');
  const { draftsCount, waitingOnArtCount } = useManualOrderBadges(locationId);
  const [draftsOpen, setDraftsOpen] = useState(false);

  return (
    <>
      <Button asChild variant="outline" className="gap-2">
        <Link to="/production/orders/new">
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('orders.manualOrder')}
        </Link>
      </Button>
      {draftsCount > 0 && locationId && (
        <Button variant="outline" className="gap-2 tabular-nums" onClick={() => setDraftsOpen(true)}>
          <FileText className="h-4 w-4" aria-hidden="true" />
          {t('orders.manual.drafts', { count: draftsCount })}
        </Button>
      )}
      {waitingOnArtCount > 0 && (
        <Button asChild variant="outline" className={QUEUE_ATTENTION_BUTTON_CLASS}>
          <Link to={WAITING_ON_ART_HREF}>
            {t('orders.manual.waitingOnArt', { count: waitingOnArtCount })}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
            <span className="font-normal">{t('issues.tabs.onHold')}</span>
          </Link>
        </Button>
      )}
      {locationId && <DraftsDialog open={draftsOpen} onOpenChange={setDraftsOpen} locationId={locationId} />}
    </>
  );
}
